import { AccessRuleEntitlementContext } from "./accessRules/models";
import { LiftAccessRuleSet } from "./accessRules/liftAccessRuleSet/models";

/**
 * Currently, sellingSeasonIds, fulfillmentSeasonIds and resortIds are all required for the query,
 * but entitlementTypeIds and description are optional.
 */
export type AccessOverviewQuery = {
  sellingSeasonIds?: number[]; // required
  fulfillmentSeasonIds?: number[]; // required
  resortIds?: number[]; // required (eventually will support other node/location types)
  entitlementTypeIds?: number[]; // optional;
  entitlementDescriptionFilter?: string; // optional;
};

export interface SellingSeason {
  sellingSeasonId: number;
  seasonDescription: string;
}

export interface FulfillmentSeason {
  fulfillmentSeasonId: number;
  seasonDescription: string;
}

export type NodeType =
  | "resort"
  | "resortGroup" // future?
  | "location"; // future?

export interface AccessNode {
  type: NodeType;
  id: string; // always string for stable UI keys
  name: string; // display name
  raw: unknown; // original model (Resort, Location, ResortGroup)
}

export interface LiftAccessRuleSearchResult {
  success: boolean;
  liftAccessRuleGroups: LiftAccessRuleSet[];
  resultLimitExceeded: boolean;
  resultLimit: number;
  actualResultCount: number;
}

export interface PersistedAccessRule {
  liftAccessRuleId: number;
}

export type ValidationError = {
  messageKey: string;
};

export interface EditableAccessRule {
  localRuleId: string;
  liftAccessRuleId: number | null;
  isNew: boolean;
  validate(): ValidationError[];
}

export function attachValidation<T extends EditableAccessRule>(
  value: Omit<T, "validate">,
  validate: (value: T) => ValidationError[],
): T {
  return {
    ...(value as T),
    validate() {
      return validate(this as T);
    },
  };
}

export interface LiftAccessRuleSaveResult {
  success: boolean;
  errorCode?: string;
  operation?: string;
  changeSetCategory?: string;
  /** The entitlement context this result is for, so a result maps back to a rule set. */
  context?: AccessRuleEntitlementContext;
  /** HTTP status of a failed save (e.g. 500, 409), when available. */
  errorStatus?: number;
  /** Human-readable failure reason (server detail / status text), when available. */
  errorMessage?: string;
  // TODO: consider returning the updated rules to ensure client is in sync with server after save
}

export interface LiftAccessRuleBatchSaveResult {
  results: LiftAccessRuleSaveResult[];
  hasFailures: boolean;
  successCount: number;
  failureCount: number;
  /** The remainder after a run stops early — nothing was sent, so not a failure. */
  notAttempted?: number;
  /**
   * The run's RequestUtcDateTime, sent on every chunk. With the user it identifies
   * the run in the audit log, so support can trace one a BA reports.
   */
  runUtcDateTime?: string;
  /** True when the run was cancelled before every request was attempted. */
  cancelled?: boolean;
  /**
   * Set when a whole request failed instead of individual rule sets — a transport
   * error, a non-2xx with no per-rule-set body, or a request the circuit breaker
   * refused to send. Those rule sets are counted in `notAttempted`, not `failureCount`:
   * nothing was examined, so reporting them as individual failures would be a lie.
   */
  stoppedReason?: {
    /** The request never left the browser (circuit breaker open). */
    notSent?: boolean;
    errorStatus?: number;
    errorMessage?: string;
  };
}
